import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, apiGet, apiSend } from "../../../lib/api.js";

/**
 * B-GTCC-A19 — register row 27 ("Branding und Tenancy Settings",
 * ticket E-GTCC-15), the reading half only.
 *
 * Measured, not assumed:
 *
 *   1. The gtcc1 template reads the WRONG scope for this row. Its
 *      branding page calls `GET /v1/tenancy/settings`
 *      (`apps/admin/src/branding/BrandingPage.tsx:244`), and that
 *      service resolves the row by the CALLING tenant
 *      (`tenancy/settings.service.ts:56-62`, `requireTenant()` plus
 *      `eq(tenants.id, ctx.tenantId)`). It can therefore never answer
 *      "je Mandant" — it answers for exactly one. B-GTCC-A15 already
 *      shows that single-tenant read in the settings view.
 *
 *   2. The per-tenant values do exist, in one platform-owner GET.
 *      `GET /v1/admin/tenants` (`admin/tenants.routes.ts:79`,
 *      `requirePlatformOwner("tenants")`) answers `{ items }` from a
 *      bare `tx.select().from(tenants)`
 *      (`admin/tenants.service.ts:128-131`), i.e. every column of the
 *      table — including the `settings` JSONB that carries logo,
 *      accent colour, locale, contact, timezone and the rest
 *      (`db/schema/tenants.ts`). One request, no per-tenant fan-out.
 *
 *   3. Raw `entitlements` are NOT the effective policy. The detail
 *      route computes `resolvedEntitlements` on top of the raw JSONB
 *      precisely because an empty object still means "approval
 *      required" for a hybrid tenant (`admin/tenants.routes.ts:100`).
 *      This view therefore does not show entitlements at all — the
 *      Tenant 360 view owns that question.
 *
 * A-GTCC-27-WRITE2 (Owner-GO 2026-09-21) adds the writing half at the
 * bottom of this file: one PATCH per target tenant against
 * `/v1/admin/tenants/:tenantId/branding` (`admin/tenant-branding.routes.ts`,
 * `requirePlatformOwner("tenants")`). The logo UPLOAD stays out — this
 * editor sends an https link or `null`, never a data URI.
 */

export interface GtccBrandingOnboarding {
  readonly firstRunCompletedAt?: string | null;
  readonly steps?: Record<string, string>;
}

/** Logo values the settings DTO accepts; see `tenancy/settings.dto.ts:44`. */
export type GtccLogoKind = "none" | "linked" | "embedded";

export interface GtccBrandingSettings {
  readonly logoUrl?: string;
  readonly accentColor?: string;
  readonly defaultLocale?: string;
  readonly contactEmail?: string;
  readonly sessionIdleCapMinutes?: number;
  readonly demoApplyAllowed?: boolean;
  readonly timezone?: string;
  readonly onboarding?: GtccBrandingOnboarding;
  readonly regionalPreferences?: {
    readonly preset?: string;
  };
}

export interface GtccBrandingTenant {
  readonly id: string;
  readonly displayName?: string | null;
  readonly slug?: string | null;
  readonly type?: string;
  readonly subscriptionStatus?: string;
  readonly visibilityMode?: string;
  readonly settings?: GtccBrandingSettings | null;
}

export interface GtccBrandingTenantsResponse {
  readonly items?: readonly GtccBrandingTenant[];
}

/**
 * `display_name` is nullable in the schema and the slug is nullable for
 * rows older than the Sprint 37 backfill, so the id is the last resort
 * rather than a fallback nobody ever sees.
 */
export function tenantLabel(tenant: GtccBrandingTenant): string {
  return tenant.displayName?.trim() || tenant.slug?.trim() || tenant.id;
}

/**
 * A logo may be an https URL or a base64 data URI of up to 200 KB
 * (`tenancy/settings.dto.ts:44`). Which of the two it is matters to an
 * operator; the image itself is not this page's business.
 */
export function logoKind(logoUrl: string | undefined): GtccLogoKind {
  if (!logoUrl) return "none";
  return logoUrl.startsWith("data:") ? "embedded" : "linked";
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Only a value that passes the same check as the writer may be handed to
 * CSS. A row written by an older tool is shown as text, never as a colour.
 */
export function isHexColor(value: string | undefined): value is string {
  return typeof value === "string" && HEX_COLOR_RE.test(value);
}

export interface GtccBrandingTally {
  readonly tenants: number;
  readonly logo: number;
  readonly accent: number;
  readonly contact: number;
  readonly timezone: number;
}

export function brandingTally(rows: readonly GtccBrandingTenant[]): GtccBrandingTally {
  let logo = 0;
  let accent = 0;
  let contact = 0;
  let timezone = 0;
  for (const row of rows) {
    const settings = row.settings ?? {};
    if (settings.logoUrl) logo += 1;
    if (settings.accentColor) accent += 1;
    if (settings.contactEmail) contact += 1;
    if (settings.timezone) timezone += 1;
  }
  return { tenants: rows.length, logo, accent, contact, timezone };
}

/** Stable order: named tenants alphabetically, then whatever has no name. */
export function sortTenants(
  rows: readonly GtccBrandingTenant[],
  locale: string,
): readonly GtccBrandingTenant[] {
  return [...rows].sort((a, b) => tenantLabel(a).localeCompare(tenantLabel(b), locale));
}

export function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

const STALE_TIME_MS = 30_000;

const BRANDING_TENANTS_KEY = ["gtcc", "branding", "tenants"] as const;

/** The one GET this view makes. */
export function useGtccBrandingTenants(): UseQueryResult<GtccBrandingTenantsResponse, Error> {
  return useQuery<GtccBrandingTenantsResponse, Error>({
    queryKey: BRANDING_TENANTS_KEY,
    queryFn: ({ signal }) =>
      apiGet<GtccBrandingTenantsResponse>("/v1/admin/tenants", undefined, signal),
    staleTime: STALE_TIME_MS,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// A-GTCC-27-WRITE2 — the branding write for ONE target tenant.
// ---------------------------------------------------------------------------

/** Same list as `SUPPORTED_LOCALES` in `tenancy/settings.dto.ts`. */
export const GTCC_BRANDING_LOCALES = ["en", "de", "fr", "ar"] as const;

/** Server rule: `sessionIdleCapMinutes` is an integer 5..120. */
export const GTCC_BRANDING_IDLE_CAP_MIN = 5;
export const GTCC_BRANDING_IDLE_CAP_MAX = 120;

/**
 * The three named unit profiles, value for value the `PRESET_DEFINITIONS`
 * of `packages/units/src/profile.ts` (schema version 1; the admin app does
 * not depend on that package). The server requires the FULL resolved
 * object, not the preset name alone, so the editor expands the choice
 * here. `custom` has no fixed fields and is therefore not offered; a
 * stored custom profile is shown and kept until the operator picks a
 * named preset or clears it.
 */
export const GTCC_BRANDING_PRESETS = ["metric", "us_customary", "uk_imperial"] as const;
export type GtccBrandingPreset = (typeof GTCC_BRANDING_PRESETS)[number];

const REGIONAL_PREFERENCES_SCHEMA_VERSION = 1;

const PRESET_FIELDS: Readonly<Record<GtccBrandingPreset, Readonly<Record<string, string>>>> = {
  metric: {
    distanceUnit: "kilometer",
    speedUnit: "kilometer_per_hour",
    durationDisplay: "hours_minutes",
    volumeUnit: "liter",
    fuelConsumptionUnit: "liter_per_100km",
    temperatureUnit: "celsius",
  },
  us_customary: {
    distanceUnit: "mile",
    speedUnit: "mile_per_hour",
    durationDisplay: "hours_minutes",
    volumeUnit: "gallon_us",
    fuelConsumptionUnit: "mile_per_gallon_us",
    temperatureUnit: "fahrenheit",
  },
  uk_imperial: {
    distanceUnit: "mile",
    speedUnit: "mile_per_hour",
    durationDisplay: "hours_minutes",
    volumeUnit: "liter",
    fuelConsumptionUnit: "mile_per_gallon_imp",
    temperatureUnit: "celsius",
  },
};

export function isBrandingPreset(value: string): value is GtccBrandingPreset {
  return (GTCC_BRANDING_PRESETS as readonly string[]).includes(value);
}

export function regionalPreferencesFor(preset: GtccBrandingPreset): Record<string, unknown> {
  return { schemaVersion: REGIONAL_PREFERENCES_SCHEMA_VERSION, preset, ...PRESET_FIELDS[preset] };
}

/** The two values of `tenants.visibility_mode` (`db/schema/visibility.ts`). */
export const GTCC_BRANDING_VISIBILITY_MODES = ["all_visible", "restricted"] as const;
export type GtccBrandingVisibilityMode = (typeof GTCC_BRANDING_VISIBILITY_MODES)[number];

export function isVisibilityMode(value: string): value is GtccBrandingVisibilityMode {
  return (GTCC_BRANDING_VISIBILITY_MODES as readonly string[]).includes(value);
}

/**
 * Form state. Every text field uses the empty string for "not set"; the
 * two switches are booleans; `visibilityMode` is the empty string only
 * for a row whose stored mode is unknown to this editor.
 */
export interface GtccBrandingDraft {
  readonly logoUrl: string;
  readonly accentColor: string;
  readonly defaultLocale: string;
  readonly contactEmail: string;
  readonly timezone: string;
  readonly sessionIdleCapMinutes: string;
  readonly preset: string;
  readonly demoApplyAllowed: boolean;
  readonly onboardingDone: boolean;
  readonly visibilityMode: string;
  readonly confirm: string;
}

export type GtccBrandingDraftKey = keyof GtccBrandingDraft;

export function draftFrom(tenant: GtccBrandingTenant): GtccBrandingDraft {
  const settings = tenant.settings ?? {};
  const cap = settings.sessionIdleCapMinutes;
  return {
    logoUrl: settings.logoUrl ?? "",
    accentColor: settings.accentColor ?? "",
    defaultLocale: settings.defaultLocale ?? "",
    contactEmail: settings.contactEmail ?? "",
    timezone: settings.timezone ?? "",
    sessionIdleCapMinutes: cap === undefined ? "" : String(cap),
    preset: settings.regionalPreferences?.preset ?? "",
    demoApplyAllowed: settings.demoApplyAllowed === true,
    onboardingDone: Boolean(settings.onboarding?.firstRunCompletedAt),
    visibilityMode: tenant.visibilityMode ?? "",
    confirm: "",
  };
}

/** The body of the PATCH; the keys are those of `tenant-branding.dto.ts`. */
export type GtccBrandingPatch = Readonly<Record<string, unknown>>;

const TEXT_KEYS = ["accentColor", "defaultLocale", "contactEmail", "timezone"] as const;

/**
 * Only what changed. A text field emptied by the operator becomes `null`,
 * which the server reads as "clear"; an untouched field is not sent at
 * all, so a value this form does not understand (a custom unit profile,
 * the wizard's per-step state) is never overwritten by accident.
 *
 * Two fields are expanded rather than copied: the unit preset becomes
 * the full resolved profile the server requires, and "onboarding done"
 * becomes a timestamp while keeping the stored per-step state; "open"
 * clears the wizard state entirely (the tenant starts the wizard again).
 */
export function brandingPatch(
  tenant: GtccBrandingTenant,
  draft: GtccBrandingDraft,
  now: () => string = () => new Date().toISOString(),
): GtccBrandingPatch {
  const before = draftFrom(tenant);
  const patch: Record<string, unknown> = {};
  const logo = draft.logoUrl.trim();
  if (logo !== before.logoUrl) patch.logoUrl = logo === "" ? null : logo;
  for (const key of TEXT_KEYS) {
    const next = draft[key].trim();
    if (next === before[key]) continue;
    patch[key] = next === "" ? null : next;
  }
  const cap = draft.sessionIdleCapMinutes.trim();
  if (cap !== before.sessionIdleCapMinutes) {
    patch.sessionIdleCapMinutes = cap === "" ? null : Number(cap);
  }
  if (draft.preset !== before.preset) {
    patch.regionalPreferences =
      draft.preset === ""
        ? null
        : isBrandingPreset(draft.preset)
          ? regionalPreferencesFor(draft.preset)
          : draft.preset;
  }
  if (draft.demoApplyAllowed !== before.demoApplyAllowed) {
    patch.demoApplyAllowed = draft.demoApplyAllowed;
  }
  if (draft.onboardingDone !== before.onboardingDone) {
    const steps = tenant.settings?.onboarding?.steps;
    patch.onboarding = draft.onboardingDone
      ? { ...(steps ? { steps } : {}), firstRunCompletedAt: now() }
      : null;
  }
  if (draft.visibilityMode !== before.visibilityMode) {
    patch.visibilityMode = draft.visibilityMode;
    if (draft.visibilityMode === "restricted") patch.confirm = draft.confirm.trim();
  }
  return patch;
}

/** True when the draft switches the target tenant TO restricted visibility. */
export function needsConfirmation(tenant: GtccBrandingTenant, draft: GtccBrandingDraft): boolean {
  return (
    draft.visibilityMode === "restricted" &&
    draft.visibilityMode !== draftFrom(tenant).visibilityMode
  );
}

export type GtccBrandingFieldError =
  | "logo_invalid"
  | "accent_invalid"
  | "email_invalid"
  | "timezone_invalid"
  | "idle_cap_invalid"
  | "preset_invalid"
  | "confirm_missing";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGO_LINK_MAX = 2048;

function isHttpsLink(value: string): boolean {
  if (value.length > LOGO_LINK_MAX || !value.startsWith("https://")) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function isIanaTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * The client mirror of the server rules; the server stays the arbiter.
 *
 * Only CHANGED fields are checked. A value an older tool stored (e.g. the
 * accent `rebeccapurple`, a data-URI logo) is not sent unless the
 * operator edits it, so it must not block saving the other fields.
 */
export function draftErrors(
  tenant: GtccBrandingTenant,
  draft: GtccBrandingDraft,
): Partial<Record<GtccBrandingDraftKey, GtccBrandingFieldError>> {
  const changed = brandingPatch(tenant, draft, () => "");
  const out: Partial<Record<GtccBrandingDraftKey, GtccBrandingFieldError>> = {};
  const logo = changed.logoUrl;
  if (typeof logo === "string" && !isHttpsLink(logo)) out.logoUrl = "logo_invalid";
  const accent = changed.accentColor;
  if (typeof accent === "string" && !isHexColor(accent)) out.accentColor = "accent_invalid";
  const email = changed.contactEmail;
  if (typeof email === "string" && !EMAIL_RE.test(email)) out.contactEmail = "email_invalid";
  const timezone = changed.timezone;
  if (typeof timezone === "string" && (timezone.length > 64 || !isIanaTimezone(timezone))) {
    out.timezone = "timezone_invalid";
  }
  const cap = changed.sessionIdleCapMinutes;
  if (
    typeof cap === "number" &&
    (!Number.isInteger(cap) || cap < GTCC_BRANDING_IDLE_CAP_MIN || cap > GTCC_BRANDING_IDLE_CAP_MAX)
  ) {
    out.sessionIdleCapMinutes = "idle_cap_invalid";
  }
  if (typeof changed.regionalPreferences === "string") out.preset = "preset_invalid";
  if (needsConfirmation(tenant, draft) && draft.confirm.trim() === "") {
    out.confirm = "confirm_missing";
  }
  return out;
}

export type GtccBrandingSaveError =
  | "invalid"
  | "confirmation_mismatch"
  | "forbidden"
  | "not_found"
  | "error";

function errorCodeOf(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const code = (body as { error?: unknown }).error;
  return typeof code === "string" ? code : undefined;
}

/** One sentence per status the route documents; anything else is generic. */
export function brandingSaveError(error: unknown): GtccBrandingSaveError {
  if (error instanceof ApiError) {
    if (error.status === 400) {
      return errorCodeOf(error.body) === "confirmation_mismatch"
        ? "confirmation_mismatch"
        : "invalid";
    }
    if (error.status === 403) return "forbidden";
    if (error.status === 404) return "not_found";
  }
  return "error";
}

export interface GtccBrandingSaveInput {
  readonly tenantId: string;
  readonly patch: GtccBrandingPatch;
}

/** The one PATCH; on success the tenant list is refetched (guard #2360). */
export function useGtccBrandingSave(): UseMutationResult<unknown, Error, GtccBrandingSaveInput> {
  const qc = useQueryClient();
  return useMutation<unknown, Error, GtccBrandingSaveInput>({
    mutationFn: ({ tenantId, patch }) =>
      apiSend<unknown>(
        "PATCH",
        `/v1/admin/tenants/${encodeURIComponent(tenantId)}/branding`,
        patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BRANDING_TENANTS_KEY }),
  });
}
